import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface GroupesElectrogenesProduct {
  id: string;
  name: string;
  mainImage: string;
  provider: string;
  description: string[];
  technicalSpecs: { [key: string]: string };
  additionalImages: string[];
  category: string;
  createdAt?: any;
  updatedAt?: any;
  moteur?: string;
  alternateur?: string;
  capotage?: string;
  coffretDeCommande?: string;
  type?: string;
  capaciteReservoir?: string;
  autonomie75Charge?: string;
  puissancePRP?: string;
  puissanceLTP?: string;
  amperage?: string;
  regime?: string;
  frequence?: string;
  tension?: string;
  facteurPuissance?: string;
  charge25?: string;
  charge50?: string;
  charge75?: string;
  charge100?: string;
  prpPrimeRunningPower?: string;
  ltpLimitedTimePower?: string;
  poids?: string;
  longueur?: string;
  largeur?: string;
  hauteur?: string;
}

const ProductDetailContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
  background: #ffffff;
  min-height: 100vh;
  padding-top: 100px;
`;

const HeaderSection = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ProductImage = styled.img`
  max-width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
  border-radius: 8px;
`;

const ProviderLogo = styled.img`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  max-width: 60px;
  height: auto;
  object-fit: contain;
`;

const ProductTitleSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ProductTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
`;

const ProductSubtitle = styled.p`
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.5;
  margin: 0;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 2px solid #dc2626;
  padding-bottom: 0.5rem;
`;

const SectionIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const SpecsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
`;

const SpecLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.85rem;
  flex-shrink: 0;
  min-width: 40%;
`;

const SpecValue = styled.span`
  color: #475569;
  font-size: 0.85rem;
  text-align: right;
  word-break: break-word;
`;

const DimensionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
`;

const DimensionLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.85rem;
`;

const DimensionValue = styled.span`
  color: #475569;
  font-size: 0.85rem;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  margin-top: 2rem;
`;

const LoadingText = styled.p`
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
`;

const GroupesElectrogenesDetailMobile: React.FC = () => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<GroupesElectrogenesProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const productData = await FirebaseService.getProduct(productId);
        if (productData) {
          setProduct(productData as GroupesElectrogenesProduct);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <ProductDetailContainer>
        <LoadingContainer>
          <LoadingText>Chargement...</LoadingText>
        </LoadingContainer>
      </ProductDetailContainer>
    );
  }

  if (error || !product) {
    return (
      <ProductDetailContainer>
        <ErrorContainer>
          <ErrorText>{error || 'Produit non trouvé'}</ErrorText>
        </ErrorContainer>
      </ProductDetailContainer>
    );
  }

  return (
    <ProductDetailContainer>
      <HeaderSection>
        <ProductImage
          src={product.mainImage || '/images/logo.png'}
          alt={product.name || 'Product Image'}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/images/logo.png';
          }}
        />
        <ProviderLogo
          src={"/images/partners/" + (product.provider || "deutz") + ".png"}
          alt={(product.provider || "DEUTZ") + " Logo"}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/images/logo.png';
          }}
        />
      </HeaderSection>

      <ProductTitleSection>
        <ProductTitle>
          {product.name || 'Groupes Electrogènes'}
        </ProductTitle>
        <ProductSubtitle>
          {product.description && product.description.length > 0
            ? product.description.join(' ')
            : 'Aucune description disponible'
          }
        </ProductSubtitle>
      </ProductTitleSection>

      <MainContent>
        <Section>
          <SectionTitle>
            <SectionIcon src="/images/groupesdetail/specification.svg" alt="Spécifications" />
            Spécifications
          </SectionTitle>
          <SpecsGrid>
            {product.moteur && (
              <SpecItem>
                <SpecLabel>Moteur</SpecLabel>
                <SpecValue>{product.moteur}</SpecValue>
              </SpecItem>
            )}
            {product.alternateur && (
              <SpecItem>
                <SpecLabel>Alternateur</SpecLabel>
                <SpecValue>{product.alternateur}</SpecValue>
              </SpecItem>
            )}
            {product.capotage && (
              <SpecItem>
                <SpecLabel>Capotage</SpecLabel>
                <SpecValue>{product.capotage}</SpecValue>
              </SpecItem>
            )}
            {product.coffretDeCommande && (
              <SpecItem>
                <SpecLabel>Coffret de Commande</SpecLabel>
                <SpecValue>{product.coffretDeCommande}</SpecValue>
              </SpecItem>
            )}
            {product.capaciteReservoir && (
              <SpecItem>
                <SpecLabel>Capacité Réservoir</SpecLabel>
                <SpecValue>{product.capaciteReservoir}</SpecValue>
              </SpecItem>
            )}
            {product.autonomie75Charge && (
              <SpecItem>
                <SpecLabel>Autonomie 75% Charge</SpecLabel>
                <SpecValue>{product.autonomie75Charge}</SpecValue>
              </SpecItem>
            )}
          </SpecsGrid>
        </Section>

        <Section>
          <SectionTitle>
            <SectionIcon src="/images/groupesdetail/dimension.svg" alt="Dimensions" />
            Dimensions
          </SectionTitle>
          <SpecsGrid>
            {product.longueur && (
              <SpecItem>
                <SpecLabel>Longueur</SpecLabel>
                <SpecValue>{product.longueur}</SpecValue>
              </SpecItem>
            )}
            {product.largeur && (
              <SpecItem>
                <SpecLabel>Largeur</SpecLabel>
                <SpecValue>{product.largeur}</SpecValue>
              </SpecItem>
            )}
            {product.hauteur && (
              <SpecItem>
                <SpecLabel>Hauteur</SpecLabel>
                <SpecValue>{product.hauteur}</SpecValue>
              </SpecItem>
            )}
            {product.poids && (
              <SpecItem>
                <SpecLabel>Poids</SpecLabel>
                <SpecValue>{product.poids}</SpecValue>
              </SpecItem>
            )}
          </SpecsGrid>
        </Section>

        <Section>
          <SectionTitle>
            <SectionIcon src="/images/groupesdetail/technical.svg" alt="Spécifications Techniques" />
            Spécifications Techniques
          </SectionTitle>
          <SpecsGrid>
            {product.puissancePRP && (
              <SpecItem>
                <SpecLabel>Puissance PRP</SpecLabel>
                <SpecValue>{product.puissancePRP}</SpecValue>
              </SpecItem>
            )}
            {product.puissanceLTP && (
              <SpecItem>
                <SpecLabel>Puissance LTP</SpecLabel>
                <SpecValue>{product.puissanceLTP}</SpecValue>
              </SpecItem>
            )}
            {product.amperage && (
              <SpecItem>
                <SpecLabel>Ampérage</SpecLabel>
                <SpecValue>{product.amperage}</SpecValue>
              </SpecItem>
            )}
            {product.regime && (
              <SpecItem>
                <SpecLabel>Régime</SpecLabel>
                <SpecValue>{product.regime}</SpecValue>
              </SpecItem>
            )}
            {product.frequence && (
              <SpecItem>
                <SpecLabel>Fréquence</SpecLabel>
                <SpecValue>{product.frequence}</SpecValue>
              </SpecItem>
            )}
            {product.tension && (
              <SpecItem>
                <SpecLabel>Tension</SpecLabel>
                <SpecValue>{product.tension}</SpecValue>
              </SpecItem>
            )}
            {product.facteurPuissance && (
              <SpecItem>
                <SpecLabel>Facteur de Puissance</SpecLabel>
                <SpecValue>{product.facteurPuissance}</SpecValue>
              </SpecItem>
            )}
            {product.charge25 && (
              <SpecItem>
                <SpecLabel>25% de charge</SpecLabel>
                <SpecValue>{product.charge25}</SpecValue>
              </SpecItem>
            )}
            {product.charge50 && (
              <SpecItem>
                <SpecLabel>50% de charge</SpecLabel>
                <SpecValue>{product.charge50}</SpecValue>
              </SpecItem>
            )}
            {product.charge75 && (
              <SpecItem>
                <SpecLabel>75% de charge</SpecLabel>
                <SpecValue>{product.charge75}</SpecValue>
              </SpecItem>
            )}
            {product.charge100 && (
              <SpecItem>
                <SpecLabel>100% de charge</SpecLabel>
                <SpecValue>{product.charge100}</SpecValue>
              </SpecItem>
            )}
          </SpecsGrid>
        </Section>
      </MainContent>
    </ProductDetailContainer>
  );
};

export default GroupesElectrogenesDetailMobile; 